import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BILLING_CYCLES = [
  { value: 'fortnightly', label: 'Fortnightly (Every 2 Weeks)' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly (Every 3 Months)' },
  { value: 'annual', label: 'Annual' }
];

const DEFAULT_FEATURES = [
  'Credits roll over',
  'Priority support',
  'Cancel anytime'
];

// Custom Confirmation Modal Component
const ConfirmationModal = ({ 
  isOpen, 
  onClose, 
  onConfirm, 
  title, 
  message, 
  confirmText = "Confirm",
  cancelText = "Cancel",
  type = "warning", // warning, danger, success, info
  icon = null,
  isProcessing = false
}) => {
  if (!isOpen) return null;

  const typeStyles = {
    warning: {
      gradient: 'from-yellow-400 via-orange-500 to-amber-500',
      bgGlow: 'bg-gradient-to-br from-yellow-50 via-orange-50 to-amber-50 dark:from-yellow-900/20 dark:via-orange-900/20 dark:to-amber-900/20',
      border: 'border-yellow-300 dark:border-yellow-600',
      confirmBtn: 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600',
      defaultIcon: '⚠️'
    },
    danger: {
      gradient: 'from-red-400 via-red-500 to-red-600',
      bgGlow: 'bg-gradient-to-br from-red-50 via-red-50 to-red-100 dark:from-red-900/20 dark:via-red-900/20 dark:to-red-800/20',
      border: 'border-red-300 dark:border-red-600',
      confirmBtn: 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700',
      defaultIcon: '🗑️'
    },
    success: {
      gradient: 'from-green-400 via-green-500 to-emerald-500',
      bgGlow: 'bg-gradient-to-br from-green-50 via-green-50 to-emerald-50 dark:from-green-900/20 dark:via-green-900/20 dark:to-emerald-900/20',
      border: 'border-green-300 dark:border-green-600',
      confirmBtn: 'bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600',
      defaultIcon: '✅'
    },
    info: {
      gradient: 'from-blue-400 via-blue-500 to-blue-600',
      bgGlow: 'bg-gradient-to-br from-blue-50 via-blue-50 to-blue-100 dark:from-blue-900/20 dark:via-blue-900/20 dark:to-blue-800/20',
      border: 'border-blue-300 dark:border-blue-600',
      confirmBtn: 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700',
      defaultIcon: 'ℹ️'
    }
  };

  const style = typeStyles[type] || typeStyles.warning;
  const displayIcon = icon || style.defaultIcon;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="relative transform transition-all duration-300 ease-out animate-scaleIn">
        <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-md mx-4 border-2 ${style.border} relative overflow-hidden`}>
          {/* Background glow */}
          <div className={`absolute inset-0 ${style.bgGlow} rounded-2xl opacity-50`}></div>
          
          {/* Content */}
          <div className="relative">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center text-3xl bg-gradient-to-br ${style.gradient} text-white shadow-lg`}>
                {displayIcon}
              </div>
            </div>

            {/* Title */}
            <h3 className={`text-xl font-bold text-center mb-3 text-transparent bg-clip-text bg-gradient-to-r ${style.gradient}`}>
              {title}
            </h3>

            {/* Message */}
            <div className={`${style.bgGlow} rounded-xl p-4 border ${style.border} mb-6`}>
              <p className="text-center text-gray-700 dark:text-gray-300">
                {message}
              </p>
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={isProcessing}
                className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-600 transition-all disabled:opacity-50"
              >
                {cancelText}
              </button>
              <button
                onClick={onConfirm}
                disabled={isProcessing}
                className={`flex-1 px-4 py-3 ${style.confirmBtn} text-white rounded-xl font-medium transition-all transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:transform-none flex items-center justify-center gap-2`}
              >
                {isProcessing ? (
                  <>
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                    </svg>
                    Processing...
                  </>
                ) : confirmText}
              </button>
            </div>
          </div>

          {/* Decorative sparkles */}
          <div className="absolute top-3 right-3 text-yellow-400 text-lg animate-pulse">✨</div>
          <div className="absolute bottom-3 left-3 text-yellow-400 text-lg animate-pulse" style={{ animationDelay: '0.5s' }}>✨</div>
        </div>
      </div>
      
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0.95); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
        .animate-fadeIn { animation: fadeIn 0.2s ease-out; }
        .animate-scaleIn { animation: scaleIn 0.3s ease-out; }
      `}</style>
    </div>
  );
};

const SubscriptionPlanManager = ({ adminToken }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    billing_cycle: 'monthly',
    credits: 0,
    price_usd: 0,
    discount_percentage: 0,
    features: [],
    is_active: true,
    is_popular: false,
    display_order: 0
  });
  const [newFeature, setNewFeature] = useState('');

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'warning',
    icon: null,
    confirmText: 'Confirm',
    onConfirm: () => {},
    isProcessing: false
  });

  useEffect(() => {
    fetchPlans();
  }, [adminToken]);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/subscriptions/admin/plans`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setPlans(response.data.plans || []);
    } catch (err) {
      setError('Failed to load subscription plans');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      billing_cycle: 'monthly',
      credits: 0,
      price_usd: 0,
      discount_percentage: 0,
      features: [...DEFAULT_FEATURES],
      is_active: true,
      is_popular: false,
      display_order: plans.length
    });
    setEditingPlan(null);
    setNewFeature('');
  };

  const handleEdit = (plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      description: plan.description || '',
      billing_cycle: plan.billing_cycle,
      credits: plan.credits,
      price_usd: plan.price_usd,
      discount_percentage: plan.discount_percentage || 0,
      features: plan.features || [],
      is_active: plan.is_active,
      is_popular: plan.is_popular || false,
      display_order: plan.display_order || 0
    });
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingPlan) {
        await axios.put(
          `${API}/subscriptions/admin/plans/${editingPlan.id}`,
          formData,
          { headers: { Authorization: `Bearer ${adminToken}` }}
        );
        setSuccess('Plan updated successfully');
      } else {
        await axios.post(
          `${API}/subscriptions/admin/plans`,
          formData,
          { headers: { Authorization: `Bearer ${adminToken}` }}
        );
        setSuccess('Plan created successfully');
      }
      
      setShowForm(false);
      resetForm();
      fetchPlans();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save plan');
    }
  };

  const closeConfirmModal = () => {
    setConfirmModal(prev => ({ ...prev, isOpen: false, isProcessing: false }));
  };

  const handleDeactivate = (plan) => {
    setConfirmModal({
      isOpen: true,
      title: 'Deactivate Plan?',
      message: `Are you sure you want to deactivate "${plan.name}"? Users will no longer be able to subscribe to this plan.`,
      type: 'warning',
      icon: '⏸️',
      confirmText: 'Deactivate',
      isProcessing: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isProcessing: true }));
        try {
          await axios.delete(`${API}/subscriptions/admin/plans/${plan.id}`, {
            headers: { Authorization: `Bearer ${adminToken}` }
          });
          setSuccess('Plan deactivated successfully');
          fetchPlans();
        } catch (err) {
          setError(err.response?.data?.detail || 'Failed to deactivate plan');
        } finally {
          closeConfirmModal();
        }
      }
    });
  };

  const handleReactivate = (plan) => {
    setConfirmModal({
      isOpen: true,
      title: 'Reactivate Plan?',
      message: `Reactivate "${plan.name}"? Users will be able to subscribe to this plan again.`,
      type: 'success',
      icon: '▶️',
      confirmText: 'Reactivate',
      isProcessing: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isProcessing: true }));
        try {
          await axios.post(`${API}/subscriptions/admin/plans/${plan.id}/reactivate`, {}, {
            headers: { Authorization: `Bearer ${adminToken}` }
          });
          setSuccess('Plan reactivated successfully');
          fetchPlans();
        } catch (err) {
          setError(err.response?.data?.detail || 'Failed to reactivate plan');
        } finally {
          closeConfirmModal();
        }
      }
    });
  };

  const handlePermanentDelete = (plan) => {
    setConfirmModal({
      isOpen: true,
      title: '⚠️ Permanently Delete?',
      message: `This will PERMANENTLY delete "${plan.name}" and cannot be undone. All plan configuration will be lost forever.`,
      type: 'danger',
      icon: '🗑️',
      confirmText: 'Delete Forever',
      isProcessing: false,
      onConfirm: async () => {
        setConfirmModal(prev => ({ ...prev, isProcessing: true }));
        try {
          await axios.delete(`${API}/subscriptions/admin/plans/${plan.id}/permanent`, {
            headers: { Authorization: `Bearer ${adminToken}` }
          });
          setSuccess('Plan permanently deleted');
          fetchPlans();
        } catch (err) {
          setError(err.response?.data?.detail || 'Failed to delete plan');
        } finally {
          closeConfirmModal();
        }
      }
    });
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, newFeature.trim()]
      });
      setNewFeature('');
    }
  };

  const removeFeature = (index) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index)
    });
  };

  const getCostPerCredit = (plan) => {
    return plan.credits > 0 ? (plan.price_usd / plan.credits).toFixed(3) : '0.000';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        icon={confirmModal.icon}
        confirmText={confirmModal.confirmText}
        isProcessing={confirmModal.isProcessing}
      />

      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Subscription Plans
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Manage recurring subscription plans for your users
          </p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <span>+</span> Add Plan
        </button>
      </div>

      {/* Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <span>❌</span> {error}
          <button onClick={() => setError('')} className="ml-auto text-red-500 hover:text-red-700">×</button>
        </div>
      )}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <span>✅</span> {success}
          <button onClick={() => setSuccess('')} className="ml-auto text-green-500 hover:text-green-700">×</button>
        </div>
      )}

      {/* Plans Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-900">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Plan</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Cycle</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Credits</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Price</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">$/Credit</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {plans.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                  No subscription plans yet. Create your first plan!
                </td>
              </tr>
            ) : (
              plans.map((plan) => (
                <tr key={plan.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${!plan.is_active ? 'opacity-60' : ''}`}>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-900 dark:text-white">{plan.name}</span>
                      {plan.is_popular && (
                        <span className="px-2 py-0.5 bg-blue-100 text-blue-800 text-xs rounded-full">Popular</span>
                      )}
                    </div>
                    {plan.description && (
                      <div className="text-sm text-gray-500 dark:text-gray-400">{plan.description}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 text-gray-600 dark:text-gray-300 capitalize">
                    {plan.billing_cycle}
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-semibold text-blue-600 dark:text-blue-400">
                      {plan.credits.toLocaleString()}
                    </span>
                  </td>
                  <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                    ${plan.price_usd}
                    {plan.discount_percentage > 0 && (
                      <span className="ml-2 text-xs text-green-600">-{plan.discount_percentage}%</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-gray-600 dark:text-gray-300">
                    ${getCostPerCredit(plan)}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      plan.is_active 
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                    }`}>
                      {plan.is_active ? '✓ Active' : '○ Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleEdit(plan)}
                        className="px-3 py-1.5 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg text-sm font-medium transition-colors"
                      >
                        ✏️ Edit
                      </button>
                      {plan.is_active ? (
                        <button
                          onClick={() => handleDeactivate(plan)}
                          className="px-3 py-1.5 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg text-sm font-medium transition-colors"
                        >
                          ⏸️ Deactivate
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => handleReactivate(plan)}
                            className="px-3 py-1.5 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg text-sm font-medium transition-colors"
                          >
                            ▶️ Reactivate
                          </button>
                          <button
                            onClick={() => handlePermanentDelete(plan)}
                            className="px-3 py-1.5 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg text-sm font-medium transition-colors"
                          >
                            🗑️ Delete
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-2xl w-full mx-4 my-8 shadow-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6">
              {editingPlan ? 'Edit Plan' : 'Create New Plan'}
            </h2>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name & Description */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Plan Name *
                  </label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="e.g., Pro Monthly"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Billing Cycle *
                  </label>
                  <select
                    value={formData.billing_cycle}
                    onChange={(e) => setFormData({...formData, billing_cycle: e.target.value})}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    {BILLING_CYCLES.map(cycle => (
                      <option key={cycle.value} value={cycle.value}>{cycle.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Brief description of the plan"
                />
              </div>

              {/* Credits & Price */}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Credits per Cycle *
                  </label>
                  <input
                    type="number"
                    value={formData.credits}
                    onChange={(e) => setFormData({...formData, credits: parseInt(e.target.value) || 0})}
                    required
                    min="0"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Price (USD) *
                  </label>
                  <input
                    type="number"
                    value={formData.price_usd}
                    onChange={(e) => setFormData({...formData, price_usd: parseFloat(e.target.value) || 0})}
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Discount %
                  </label>
                  <input
                    type="number"
                    value={formData.discount_percentage}
                    onChange={(e) => setFormData({...formData, discount_percentage: parseInt(e.target.value) || 0})}
                    min="0"
                    max="100"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>

              {/* Cost per credit preview */}
              {formData.credits > 0 && formData.price_usd > 0 && (
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3 text-sm">
                  <span className="text-blue-800 dark:text-blue-200">
                    Cost per credit: <strong>${(formData.price_usd / formData.credits).toFixed(3)}</strong>
                  </span>
                </div>
              )}

              {/* Features */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Features
                </label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={newFeature}
                    onChange={(e) => setNewFeature(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
                    className="flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="Add a feature..."
                  />
                  <button
                    type="button"
                    onClick={addFeature}
                    className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg dark:bg-gray-700 dark:hover:bg-gray-600"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.features.map((feature, idx) => (
                    <span
                      key={idx}
                      className="inline-flex items-center px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-sm"
                    >
                      {feature}
                      <button
                        type="button"
                        onClick={() => removeFeature(idx)}
                        className="ml-2 text-gray-500 hover:text-red-500"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Options */}
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_popular}
                    onChange={(e) => setFormData({...formData, is_popular: e.target.checked})}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Mark as Popular</span>
                </label>
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-700 dark:text-gray-300">Display Order:</label>
                  <input
                    type="number"
                    value={formData.display_order}
                    onChange={(e) => setFormData({...formData, display_order: parseInt(e.target.value) || 0})}
                    min="0"
                    className="w-20 px-2 py-1 border rounded focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  {editingPlan ? 'Update Plan' : 'Create Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscriptionPlanManager;
